import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Layout, showToast } from '@components/Layout';
import { Button, Card } from '@/components/UI';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useAuth } from '@/context/AuthContext';
import SupportRestrictedFeatureCard from '@/components/Support/SupportRestrictedFeatureCard';

interface BrokerConnectionRow {
    id: number;
    brokerage: 'TradeStation' | 'Schwab' | 'Alpaca';
    name: string;
    status: 'active' | 'expired' | 'revoked' | 'error' | 'reauthorizationRequired' | 'invalid' | 'disconnected';
    authMethod: 'oauth' | 'apiKeys';
    ownershipMode: 'platformApp' | 'userApp' | 'userKeys';
    environmentScope: 'live' | 'paper' | 'both';
    updatedDateTime: string;
}

const NEXT_STEPS = [
    'You will be redirected to your brokerage provider to approve access.',
    'After approval, you will return to Project314.',
    'From there, you can import your brokerage account and continue setup.'
] as const;

const ACCESS_ITEMS = ['Account balances', 'Positions and orders', 'Trading workflows inside Project314'] as const;

function getStatusLabel(status: BrokerConnectionRow['status']) {
    if (status === 'active') return 'Connected';
    if (status === 'reauthorizationRequired') return 'Needs Reauthorization';
    return status.charAt(0).toUpperCase() + status.slice(1);
}

function getStatusClasses(status: BrokerConnectionRow['status'] | 'signedOut' | 'notConnected') {
    switch (status) {
        case 'active':
            return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
        case 'reauthorizationRequired':
        case 'expired':
            return 'border-amber-500/30 bg-amber-500/10 text-amber-300';
        case 'signedOut':
        case 'notConnected':
            return 'border-sky-500/30 bg-sky-500/10 text-sky-300';
        case 'revoked':
        case 'error':
        case 'invalid':
        case 'disconnected':
            return 'border-rose-500/30 bg-rose-500/10 text-rose-300';
    }
}

const ConnectBrokerage = () => {
    const [error, setError] = useState<string | null>(null);
    const [pendingOauthRequest, setPendingOauthRequest] = useState<{ brokerConnectionId: number } | null>(null);

    const { isAuthenticated, isLoading, sessionPolicy, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { canManageBrokerConnections } = sessionPolicy;

    const brokerConnectionsQuery = useAppQuery({
        ...trpc.user.fetchBrokerConnections.queryOptions(),
        enabled: isAuthenticated && !isLoading && canManageBrokerConnections
    });

    useEffect(() => {
        if (!brokerConnectionsQuery.error) return;

        handleTrpcQuery(brokerConnectionsQuery, {
            fallbackErrorMessage: 'Failed to load brokerage connection details',
            showErrorToast: true,
            setError: (message) => setError(message)
        });
    }, [brokerConnectionsQuery.error]);

    const oauthUrlQuery = useAppQuery({
        ...trpc.user.fetchOauthUrl.queryOptions({
            service: 'Alpaca',
            brokerConnectionId: pendingOauthRequest?.brokerConnectionId ?? null
        }),
        enabled: canManageBrokerConnections && pendingOauthRequest !== null
    });

    useEffect(() => {
        if (!oauthUrlQuery.error) return;

        handleTrpcQuery(oauthUrlQuery, {
            fallbackErrorMessage: 'Failed to start brokerage authorization',
            showErrorToast: true,
            setError: (message) => setError(message)
        });

        const timeoutId = window.setTimeout(() => {
            setPendingOauthRequest(null);
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [oauthUrlQuery.error]);

    useEffect(() => {
        if (oauthUrlQuery.data?.url) {
            window.location.href = oauthUrlQuery.data.url;
        }
    }, [oauthUrlQuery.data]);

    const saveBrokerConnectionMutation = useTrpcMutation(
        trpc.user.saveBrokerConnection.mutationOptions({
            onSuccess: async (data) => {
                await queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchBrokerConnections.queryOptions().queryKey
                });

                if (!data.success) return;

                if (data.requiresOauthAuthorization) {
                    setPendingOauthRequest({
                        brokerConnectionId: data.brokerConnectionId
                    });

                    return;
                }

                showToast('Brokerage connection is ready for this Project314 account.', 'success');
            }
        }),
        {
            fallbackErrorMessage: 'Failed to prepare brokerage connection'
        }
    );

    const brokerConnections = (brokerConnectionsQuery.data?.brokerConnections ?? []) as BrokerConnectionRow[];

    const brokerageOauthConnection = useMemo(() => {
        const matches = brokerConnections.filter(
            (connection) =>
                connection.brokerage === 'Alpaca' && connection.authMethod === 'oauth' && connection.ownershipMode === 'platformApp'
        );

        const activeConnection = matches.find((connection) => connection.status === 'active');
        return activeConnection ?? matches[0] ?? null;
    }, [brokerConnections]);

    const isBusy = saveBrokerConnectionMutation.isPending || oauthUrlQuery.isFetching;
    const redirectUrl = `${location.pathname}${location.search}`;

    const handleConnect = async () => {
        setError(null);

        if (!isAuthenticated) {
            void navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
            return;
        }

        if (brokerageOauthConnection) {
            setPendingOauthRequest({
                brokerConnectionId: brokerageOauthConnection.id
            });

            return;
        }

        await saveBrokerConnectionMutation.mutateAsync({
            brokerage: 'Alpaca',
            name: 'Brokerage Account',
            authMethod: 'oauth',
            ownershipMode: 'platformApp',
            environmentScope: 'both'
        });
    };

    const primaryLabel = !isAuthenticated ? 'Log In to Continue' : isBusy ? 'Redirecting' : 'Connect Account';

    const statusLabel = !isAuthenticated
        ? 'Sign In Required'
        : brokerageOauthConnection
          ? getStatusLabel(brokerageOauthConnection.status)
          : 'Ready to Connect';

    const statusClasses = getStatusClasses(!isAuthenticated ? 'signedOut' : (brokerageOauthConnection?.status ?? 'notConnected'));

    if (isAuthenticated && !canManageBrokerConnections) {
        return (
            <Layout hideHeaderUntilAuthenticated={false} title="Connect Your Brokerage Account">
                <SupportRestrictedFeatureCard
                    description="Brokerage connection authorization is disabled during a read-only support session because it can create or refresh external account access."
                    title="Brokerage Connection Is Unavailable"
                />
            </Layout>
        );
    }

    return (
        <Layout hideHeaderUntilAuthenticated={false} title="Connect Your Brokerage Account">
            <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <p className="text-sm font-medium uppercase tracking-[0.22em] text-zinc-500">Brokerage Connection</p>
                    <h1 className="mt-3 text-4xl font-semibold text-white">Connect your brokerage account</h1>
                    <p className="mt-4 max-w-3xl text-base leading-7 text-zinc-400">
                        Securely link your brokerage account to Project314 so you can import accounts, review balances and positions, and
                        use trading workflows inside the platform.
                    </p>
                </div>

                <div className="grid gap-6 lg:grid-cols-[1.35fr_0.9fr]">
                    <Card
                        bodyClassName="space-y-6"
                        subtitle="Authorize access with your brokerage provider, then return to Project314 to continue setup."
                        title="Start connection"
                    >
                        <div className="flex flex-wrap items-center gap-3">
                            <span className={`rounded-full border px-3 py-1 text-sm font-medium ${statusClasses}`}>{statusLabel}</span>
                            {user && <span className="text-sm text-zinc-500">Signed in as {user.email}</span>}
                        </div>

                        <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4">
                            <h2 className="text-lg font-medium text-white">What happens next</h2>
                            <ul className="mt-4 space-y-3 text-sm leading-6 text-zinc-400">
                                {NEXT_STEPS.map((step) => (
                                    <li className="flex items-start gap-3" key={step}>
                                        <span className="mt-2 h-2 w-2 rounded-full bg-p314-blue" />
                                        <span>{step}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row">
                            <Button isLoading={isBusy} size="lg" onClick={handleConnect}>
                                {primaryLabel}
                            </Button>
                            {!isAuthenticated ? (
                                <Button size="lg" to="/register" variant="outline-dark">
                                    Create Account
                                </Button>
                            ) : (
                                <Button size="lg" to="/user/tradingAccounts" variant="outline-dark">
                                    Trading Accounts
                                </Button>
                            )}
                        </div>

                        <p className="text-sm leading-6 text-zinc-500">
                            You will complete authorization on your brokerage provider’s site. Project314 does not ask for your brokerage
                            password on this page.
                        </p>

                        {error && (
                            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                                {error}
                            </div>
                        )}
                    </Card>

                    <div className="space-y-6">
                        <Card title="Connection details">
                            <dl className="space-y-4 text-sm">
                                <div className="flex items-center justify-between gap-4">
                                    <dt className="text-zinc-500">Connection type</dt>
                                    <dd className="text-white">Brokerage authorization</dd>
                                </div>
                                <div className="flex items-center justify-between gap-4">
                                    <dt className="text-zinc-500">Access scope</dt>
                                    <dd className="text-white">
                                        {brokerageOauthConnection?.environmentScope === 'both'
                                            ? 'Live + Paper'
                                            : (brokerageOauthConnection?.environmentScope ?? 'Live + Paper')}
                                    </dd>
                                </div>
                                <div className="flex items-center justify-between gap-4">
                                    <dt className="text-zinc-500">Status</dt>
                                    <dd className="text-white">{statusLabel}</dd>
                                </div>
                                {brokerageOauthConnection && (
                                    <div className="flex items-center justify-between gap-4">
                                        <dt className="text-zinc-500">Last updated</dt>
                                        <dd className="text-white">{brokerageOauthConnection.updatedDateTime}</dd>
                                    </div>
                                )}
                            </dl>
                        </Card>

                        <Card title="Access requested">
                            <ul className="space-y-3 text-sm leading-6 text-zinc-400">
                                {ACCESS_ITEMS.map((item) => (
                                    <li className="flex items-start gap-3" key={item}>
                                        <span className="mt-2 h-2 w-2 rounded-full bg-emerald-400" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ConnectBrokerage;
